// The component is responsible for displaying individual items or
// services within the slider. Each tile includes an image, name,
// link, with clickable elements wrapped in Link
// components for navigation. Its purpose is to provide users with detailed information about
// a specific service or product, making it visually accessible and easily navigable within
// the carousel structure.

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { useFormat } from 'helpers/hooks/useFormat';
import { CarouselContentItem, carouselOverlayContent, carouselOverlayText, Overlay } from 'types/next';
import { SmartLink } from 'components/commercetools-ui/atoms/smart-link';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop, tablet, mobile, xxs } from 'helpers/utils/screensizes';
import { getPositionClass } from '../helper';

interface TileProps {
  data: CarouselContentItem;
  locale: 'en-GB' | 'en-EU';
}

const CategoryTile: React.FC<TileProps> = ({ data, locale }) => {
  const { formatMessage } = useFormat({ name: 'common' });
  const [isDesktopSize] = useMediaQuery(desktop);
  const [isTablet] = useMediaQuery(tablet);
  const [ismobile] = useMediaQuery(xxs);
  const [currentPositionClass, setCurrentPositionClass] = useState<string>('');
  const image = data?.image?.[locale];
  const link = data?.link?.[locale] || '';
  const name = data?.name?.[locale] || '';

  useEffect(() => {
    if (data) {
      const position = isDesktopSize ? data?.desktopPosition || '' : data?.mobilePosition || '';
      setCurrentPositionClass(getPositionClass(position));
    }
  }, [data, ismobile]);

  const overlayTextText = data?.overlayText?.find((overlay: carouselOverlayText) =>
    isDesktopSize
      ? overlay?.screentype === 'desktop'
      : isTablet
      ? overlay?.screentype === 'tablet'
      : ismobile
      ? overlay?.screentype === 'mobile'
      : overlay?.screentype === 'desktop',
  );
  return (
    <div className="flex flex-col items-start justify-start">
      <a
        href={link || ''}
        target={data?.linkTarget === '_blank' ? '_blank' : '_self'}
        rel={data?.linkTarget === '_blank' ? 'noopener noreferrer' : undefined}
        onClick={(e) => {
          if (data?.linkTarget === '_newwindow') {
            e.preventDefault();
            window.open(link, '_blank', 'noopener,noreferrer,width=800,height=600');
          }
        }}
        lang={locale}
        aria-label={name}
      >
        {image && (
          <img
            src={`https://${image?.defaultHost}/i/${image?.endpoint}/${image?.name}?fmt=webp`}
            alt={name}
            className="h-[100%] w-[100%] "
          />
        )}
        {currentPositionClass && (
          <div className={`absolute ${currentPositionClass} w-full `}>
            <div className={`flex w-full flex-col `}>
              {overlayTextText && (
                <div className="flex w-full  flex-col">
                  {overlayTextText && (
                    <>
                      {overlayTextText?.overlayContent?.map((content: carouselOverlayContent, index: number) => {
                        const paragraphText = content?.textContent?.paragraph?.[locale] || '';
                        const headingTag = content?.textContent?.heading?.htmlTag || 'h2';
                        const fontWeight = content?.textContent?.heading?.fontWeight || '600';
                        // Create the heading element dynamically based on the selected HTML tag
                        const HeadingTag = headingTag as keyof JSX.IntrinsicElements;

                        return (
                          <div className={`flex flex-col ${content?.textAlignmentPosition}`} key={index}>
                            <HeadingTag
                              className="pb-[0.5rem] font-body font-bold leading-none md:pb-[0.25rem]"
                              key={index}
                              style={{
                                color: content?.textContent?.heading?.textColour,
                                fontSize: content?.textContent?.heading?.textSize
                                  ? `${content?.textContent?.heading?.textSize}px`
                                  : '20px',
                                fontWeight: fontWeight,
                              }}
                            >
                              {content?.textContent?.heading?.[locale] || ''}
                            </HeadingTag>

                            <p
                              className="pb-[0.5rem] font-body"
                              style={{
                                color: content?.textContent?.paragraph?.textColour || 'black',
                                fontSize: content?.textContent?.paragraph?.textSize
                                  ? `${content?.textContent?.paragraph?.textSize}px`
                                  : '20px',
                              }}
                            >
                              {paragraphText}
                            </p>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              )}
              <div
                className={`
                    grid
                    w-full
                    gap-[1rem]
                    text-center
                    ${isDesktopSize ? ' grid-cols-2' : ''}
                    ${isTablet ? (data?.cta?.length > 4 ? 'grid-cols-2' : 'grid-cols-2') : ''}
                    ${ismobile && data?.cta?.length === 1 ? 'grid-cols-1' : ismobile ? 'grid-cols-2' : ''}
                  `}
              >
                {data &&
                  data?.cta?.length > 0 &&
                  data?.cta?.map((button: Overlay, index: number) => {
                    // const isTabletSix =
                    //   !isDesktopSize && isTablet && data?.overlay?.length === 6 && (index === 4 || index === 5);
                    return (
                      <a
                        href={button?.link || '#'}
                        key={index}
                        target={button?.linkTarget === '_blank' ? '_blank' : '_self'}
                        rel={button?.linkTarget === '_blank' ? 'noopener noreferrer' : undefined}
                        onClick={(e) => {
                          if (button?.linkTarget === '_newwindow') {
                            e.preventDefault();
                            window.open(button.link, '_blank', 'noopener,noreferrer,width=800,height=600');
                          }
                        }}
                        // className={`w-full ${isTabletSix ? 'col-span-2' : ''}`}
                        className="w-full"
                        lang={locale}
                        aria-label={button?.text?.[locale] || ''}
                      >
                        <button
                          className={`
                            flex h-[40px] w-full items-center
                            justify-center rounded-sm font-bold uppercase
                            text-black
                           
                          `}
                          style={{
                            backgroundColor: button?.buttonBackgroundColour,
                            whiteSpace: 'nowrap',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = button?.HoverButtonBackgroundColour;
                            e.currentTarget.style.color = button?.hoverButtonTextColour;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = button?.buttonBackgroundColour;
                            e.currentTarget.style.color = 'black'; // Default text color
                          }}
                        >
                          {button?.text?.[locale]}
                        </button>
                      </a>
                    );
                  })}
              </div>
            </div>
          </div>
        )}
        {currentPositionClass && (
          <div className={`absolute ${currentPositionClass} w-full `}>
            <div className={`flex w-full flex-col `}>
              {overlayTextText && (
                <div className="flex w-full  flex-col">
                  {overlayTextText && (
                    <>
                      {overlayTextText?.overlayContent?.map((content: carouselOverlayContent, index: number) => {
                        const paragraphText = content?.textContent?.paragraph?.[locale] || '';
                        const headingTag = content?.textContent?.heading?.htmlTag || 'h2';
                        const fontWeight = content?.textContent?.heading?.fontWeight || '600';
                        // Create the heading element dynamically based on the selected HTML tag
                        const HeadingTag = headingTag as keyof JSX.IntrinsicElements;

                        return (
                          <div className={`flex flex-col ${content?.textAlignmentPosition}`} key={index}>
                            <HeadingTag
                              className="pb-[0.5rem] font-body font-bold leading-none md:pb-[0.25rem]"
                              key={index}
                              style={{
                                color: content?.textContent?.heading?.textColour,
                                fontSize: content?.textContent?.heading?.textSize
                                  ? `${content?.textContent?.heading?.textSize}px`
                                  : '20px',
                                fontWeight: fontWeight,
                              }}
                            >
                              {content?.textContent?.heading?.[locale] || ''}
                            </HeadingTag>

                            <p
                              className="font-body"
                              style={{
                                color: content?.textContent?.paragraph?.textColour || 'black',
                                fontSize: content?.textContent?.paragraph?.textSize
                                  ? `${content?.textContent?.paragraph?.textSize}px`
                                  : '20px',
                              }}
                            >
                              {paragraphText}
                            </p>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              )}
              <div
                className={`
                    grid
                    w-full
                    gap-[1rem]
                    text-center
                    ${isDesktopSize ? ' grid-cols-2' : ''}
                    ${isTablet ? (data?.cta?.length > 4 ? 'grid-cols-2' : 'grid-cols-2') : ''}
                    ${ismobile && data?.cta?.length === 1 ? 'grid-cols-1' : ismobile ? 'grid-cols-2' : ''}
                  `}
              >
                {data &&
                  data?.cta?.length > 0 &&
                  data?.cta?.map((button: Overlay, index: number) => {
                    // const isTabletSix =
                    //   !isDesktopSize && isTablet && data?.overlay?.length === 6 && (index === 4 || index === 5);
                    return (
                      <a
                        href={button?.link || '#'}
                        key={index}
                        target={button?.linkTarget === '_blank' ? '_blank' : '_self'}
                        rel={button?.linkTarget === '_blank' ? 'noopener noreferrer' : undefined}
                        onClick={(e) => {
                          if (button?.linkTarget === '_newwindow') {
                            e.preventDefault();
                            window.open(button.link, '_blank', 'noopener,noreferrer,width=800,height=600');
                          }
                        }}
                        // className={`w-full ${isTabletSix ? 'col-span-2' : ''}`}
                        className="w-full"
                        lang={locale}
                        aria-label={button?.text?.[locale] || ''}
                      >
                        <button
                          className={`
                            flex h-[40px] w-full items-center
                            justify-center rounded-sm font-bold uppercase
                            text-black
                           
                          `}
                          style={{
                            backgroundColor: button?.buttonBackgroundColour,
                            whiteSpace: 'nowrap',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = button?.HoverButtonBackgroundColour;
                            e.currentTarget.style.color = button?.hoverButtonTextColour;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = button?.buttonBackgroundColour;
                            e.currentTarget.style.color = 'black'; // Default text color
                          }}
                        >
                          {button?.text?.[locale]}
                        </button>
                      </a>
                    );
                  })}
              </div>
            </div>
          </div>
        )}
      </a>
      <h2 className="mt-15 h-24 pb-15 font-body text-20 font-bold uppercase leading-6">{name}</h2>
    </div>
  );
};

export default CategoryTile;
